import { useParams, Link } from "react-router-dom";
import { Clock, Check, AlertTriangle, ArrowUpRight, MessageCircle, CalendarCheck, Phone, MapPin, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useService } from "@/lib/queries";
import { useTrackView } from "@/lib/useTrackView";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink, telLink, serviceWhatsAppMessage } from "@/lib/contact";
import { serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";
import NotFound from "@/pages/NotFound";
import { ExtraSections } from "@/components/ExtraSections";

function Block({ number, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        {number && <span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">{number}</span>}
        <h2 className="font-display text-2xl font-medium text-white sm:text-[1.7rem]">{title}</h2>
      </div>
      <div className="mt-4 text-base leading-relaxed text-neutral-300">{children}</div>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: s, isLoading, isError } = useService(slug);
  const settings = useSettings();
  useTrackView("service", slug);

  if (isError) return <NotFound />;
  if (isLoading || !s) return <div className="flex min-h-screen items-center justify-center text-neutral-500">Yükleniyor…</div>;

  const waMessage = serviceWhatsAppMessage(s.name);
  const crumbs = [{ label: "Ana Sayfa", to: "/" }, { label: "Hizmetler", to: "/hizmetler" }, { label: s.name }];
  const schema = [
    serviceSchema({ name: s.h1 || s.name, description: s.seo_description || s.excerpt, url: `/hizmetler/${s.slug}`, image: s.hero_image }),
    breadcrumbSchema(crumbs),
  ];
  const fq = faqSchema(s.faqs || []);
  if (fq) schema.push(fq);

  return (
    <>
      <Seo title={s.seo_title || s.name} description={s.seo_description || s.excerpt} image={s.og_image || s.hero_image} canonicalPath={`/hizmetler/${s.slug}`} jsonLd={schema} />
      <PageHero overline="Hizmet" title={s.h1 || s.name} subtitle={s.excerpt} image={s.hero_image} breadcrumbs={crumbs}>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/randevu?hizmet=${encodeURIComponent(s.name)}`} className="inline-flex items-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300" data-testid="service-randevu-btn">
            <CalendarCheck className="h-[18px] w-[18px]" /> Hızlı Randevu Al
          </Link>
          <a href={whatsappLink(settings, waMessage)} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn" className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] px-6 py-3.5 font-oswald text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold/50">
            <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Yaz
          </a>
        </div>
      </PageHero>

      <section className="obsidian py-16 sm:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-[1.6fr_0.9fr]">
          {/* Main */}
          <div className="space-y-14">
            <Reveal><Block number="01" title={`${s.name} nedir?`}><p>{s.intro}</p><p className="mt-4">{s.what_is}</p></Block></Reveal>
            {s.who_for && <Reveal><Block number="02" title="Kimler için uygundur?"><p>{s.who_for}</p></Block></Reveal>}
            {s.when_to && <Reveal><Block number="03" title="Ne zaman yaptırılmalıdır?"><p>{s.when_to}</p></Block></Reveal>}

            {s.process?.length > 0 && (
              <Reveal>
                <Block number="04" title="Uygulama süreci">
                  <ol className="mt-4 space-y-2.5">
                    {s.process.map((p, i) => (
                      <li key={i} className="flex gap-4 border border-white/10 bg-white/[0.02] p-5 transition-colors hover:border-gold/30">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-gold font-display font-semibold text-black">{i + 1}</span>
                        <span>
                          <span className="block font-display text-lg font-medium text-white">{p.title}</span>
                          <span className="mt-1 block text-sm text-neutral-400">{p.desc}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </Block>
              </Reveal>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {s.benefits?.length > 0 && (
                <Reveal>
                  <div className="h-full border border-gold/25 bg-gold/[0.04] p-6">
                    <h3 className="font-display text-xl font-medium text-white">Faydaları</h3>
                    <ul className="mt-4 space-y-3">
                      {s.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-neutral-200"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {b}</li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}
              {s.cautions?.length > 0 && (
                <Reveal delay={0.1}>
                  <div className="h-full border border-white/10 bg-white/[0.02] p-6">
                    <h3 className="font-display text-xl font-medium text-white">Dikkat edilmesi gerekenler</h3>
                    <ul className="mt-4 space-y-3">
                      {s.cautions.map((c, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-neutral-300"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" /> {c}</li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}
            </div>

            <ExtraSections sections={s.extra_sections} waMessage={waMessage} />

            {s.gallery?.length > 0 && (
              <Reveal>
                <Block number="05" title="Uygulama galerisi">
                  <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                    {s.gallery.map((g, i) => (
                      <div key={i} className="aspect-square overflow-hidden border border-white/10">
                        <img src={g} alt={`${s.name} uygulama ${i + 1}`} loading="lazy" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-500 hover:scale-110 hover:grayscale-0" />
                      </div>
                    ))}
                  </div>
                </Block>
              </Reveal>
            )}

            {/* İlgili Uygulamalar */}
            {s.case_studies?.length > 0 && (
              <Reveal>
                <Block number="06" title="Bu hizmetin gerçek uygulamaları">
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {s.case_studies.map((c) => (
                      <Link key={c.slug} to={`/uygulamalar/${c.slug}`} data-testid={`service-application-${c.slug}`} className="group flex items-center gap-4 overflow-hidden border border-white/10 bg-neutral-900 p-3 transition-colors hover:border-gold/40">
                        <img src={c.cover_image || c.after_image} alt={c.title} loading="lazy" className="h-20 w-24 shrink-0 object-cover grayscale-[0.2] transition group-hover:grayscale-0" />
                        <span className="min-w-0">
                          {c.brand && <span className="block font-oswald text-[10px] uppercase tracking-[0.15em] text-gold">{c.brand} {c.model}</span>}
                          <span className="block font-medium text-white transition-colors group-hover:text-gold">{c.title}</span>
                          {c.duration && <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-500"><Clock className="h-3 w-3" /> {c.duration}</span>}
                        </span>
                      </Link>
                    ))}
                  </div>
                </Block>
              </Reveal>
            )}

            {/* İlgili Bloglar (YENİ) */}
            {s.related_blogs?.length > 0 && (
              <Reveal>
                <Block number="07" title="İlgili rehber yazıları">
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {s.related_blogs.map((b) => (
                      <Link key={b.slug} to={`/blog/${b.slug}`} data-testid={`service-blog-${b.slug}`} className="group flex items-center gap-4 overflow-hidden border border-white/10 bg-neutral-900 p-3 transition-colors hover:border-gold/40">
                        <img src={b.cover_image} alt={b.title} loading="lazy" className="h-20 w-24 shrink-0 object-cover grayscale-[0.2] transition group-hover:grayscale-0" />
                        <span className="min-w-0">
                          <span className="inline-flex items-center gap-1.5 font-oswald text-[10px] uppercase tracking-[0.15em] text-gold"><BookOpen className="h-3 w-3" /> Rehber</span>
                          <span className="mt-0.5 block font-medium leading-snug text-white transition-colors group-hover:text-gold line-clamp-2">{b.title}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </Block>
              </Reveal>
            )}

            {s.faqs?.length > 0 && (
              <Reveal>
                <Block number="08" title="Sık sorulan sorular">
                  <Accordion type="single" collapsible className="mt-4 w-full border-t border-white/10">
                    {s.faqs.map((f, i) => (
                      <AccordionItem key={i} value={`f-${i}`} className="border-white/10">
                        <AccordionTrigger className="py-4 text-left font-display text-base font-medium text-white hover:no-underline hover:text-gold [&[data-state=open]]:text-gold">{f.question}</AccordionTrigger>
                        <AccordionContent className="pb-4 text-sm text-neutral-400">{f.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Block>
              </Reveal>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:h-fit">
            <div className="border border-white/10 bg-gradient-to-b from-neutral-900 to-[#070707] p-6">
              <h3 className="font-display text-xl font-medium text-white">Hızlı bilgi & randevu</h3>
              {s.duration && (
                <div className="mt-5 flex items-center gap-3 border border-white/10 bg-white/[0.02] p-4">
                  <Clock className="h-5 w-5 text-gold" />
                  <span><span className="block text-xs text-neutral-500">Uygulama süresi</span><span className="font-semibold text-white">{s.duration}</span></span>
                </div>
              )}
              <div className="mt-4 space-y-2.5">
                <Link to={`/randevu?hizmet=${encodeURIComponent(s.name)}`} className="flex w-full items-center justify-center gap-2 bg-gold px-5 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300">
                  <CalendarCheck className="h-[18px] w-[18px]" /> Randevu Al
                </Link>
                <a href={whatsappLink(settings, waMessage)} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40">
                  <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Yaz
                </a>
                <a href={telLink(settings)} className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40">
                  <Phone className="h-[18px] w-[18px] text-gold" /> {settings?.phone}
                </a>
              </div>
            </div>

            {s.related_services?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-medium text-white">İlgili hizmetler</h3>
                <ul className="mt-4 space-y-2.5">
                  {s.related_services.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/hizmetler/${r.slug}`} data-testid={`related-service-${r.slug}`} className="group flex items-center justify-between gap-2 border border-white/10 px-4 py-3 text-sm font-medium text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">
                        {r.name} <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>

        {/* Bölgesel / İlçe SEO sayfaları (YENİ) */}
        {s.district_links?.length > 0 && (
          <div className="container-x mt-16">
            <Reveal>
              <div className="border border-white/10 bg-black p-7 sm:p-9">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gold" />
                  <h2 className="font-display text-xl font-medium text-white sm:text-2xl">{s.name} — bölgenizde</h2>
                </div>
                <p className="mt-2 text-sm text-neutral-400">Kocaeli ilçelerinde {s.name.toLowerCase()} hizmetimiz. Bölgenize özel sayfayı inceleyin.</p>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {s.district_links.map((d) => (
                    <Link key={d.slug} to={`/${d.slug}`} data-testid={`service-district-${d.slug}`} className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.02] px-4 py-2.5 text-sm text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">
                      <MapPin className="h-3.5 w-3.5 text-gold/70" /> {d.h1 || `${s.name} ${d.district}`}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        )}
      </section>
      <FinalCta />
    </>
  );
}
